import { indicators } from '../pages/dashboards/dashboardFramework/maps/constants';

/**
 * Helper function to extract active filters from legend state based on dashboard type
 * This function extracts filter state from various dashboard legend states and returns
 * a normalized structure for analytics tracking purposes.
 *
 * @param {string} viewOnMap - Dashboard/indicator name (e.g., 'Area development', 'Property market', 'Population structure')
 * @param {any} legendState - Legend state object containing dashboard-specific state:
 *   - For Area development: { areaDevLegend: {...} }
 *   - For Property market: { propertyMarketState: {...} }
 *   - For Residential demand-supply: { residentialMarketState: {...} }
 *   - For Population structure: { populationStructureState: {...} }
 *   - For Tenant search: { tenantSearchState: {...} }
 *   - For Services mix: { serviceMixState: {...} }
 *   - For Footfall: { footfallState: {...} }
 *   - For Advanced household types: { householdTypesState: {...} }
 * @returns {Record<string, any>} Normalized object containing all active filters for the dashboard
 *
 * @example
 * const activeFilters = getActiveMapFilters(
 *   'Property market',
 *   { propertyMarketState: { selectedTab: 'properties', selectedFillColorFilter: [...], ... } }
 * )
 */
export const getActiveMapFilters = (
  viewOnMap: string,
  legendState: any,
): Record<string, any> => {
  if (!viewOnMap || !legendState) {
    return {};
  }

  switch (viewOnMap) {
    case indicators.areaDevelopment: {
      const { areaDevLegend } = legendState;
      if (!areaDevLegend) return {};

      return {
        selectedTab: areaDevLegend.selectedTab,
        activePropertyFilters: areaDevLegend.activePropertyFilters || [],
        plotFeatureType: areaDevLegend.plotFeatureType || [],
        plotLandEfficiency: areaDevLegend.plotLandEfficiency || [],
        plotSize: areaDevLegend.plotSize || [],
        plotBuildPotential: areaDevLegend.plotBuildPotential || [],
        propertyZoningType: areaDevLegend.propertyZoningType || [],
        propertyZoningSubType: areaDevLegend.propertyZoningSubType || [],
        constructionProjectType: areaDevLegend.constructionProjectType || [],
        showBuildings: areaDevLegend.showBuildings || [],
        buildingAge: areaDevLegend.buildingAge || [],
        buildingUse: areaDevLegend.buildingUse || [],
        buildingFloorArea: areaDevLegend.buildingFloorArea || [],
        buildingHeight: areaDevLegend.buildingHeight || [],
        zoningChangeRangeFilter: areaDevLegend.zoningChangeRangeFilter || [],
        remainingBuildingRightsFilter:
          areaDevLegend.remainingBuildingRightsFilter || [],
        areaExpansionFilter: areaDevLegend.areaExpansionFilter || [],
      };
    }

    case indicators.services: {
      const { serviceMixState } = legendState;
      if (!serviceMixState) return {};

      return {
        selectedTab: serviceMixState.selectedTab,
        reachabilityCategoryFilter:
          serviceMixState.reachabilityCategoryFilter || [],
        reachabilitySubCategoryFilter:
          serviceMixState.reachabilitySubCategoryFilter || [],
        serviceDiversityCategoryFilter:
          serviceMixState.serviceDiversityCategoryFilter || [],
        serviceDiversitySubCategoryFilter:
          serviceMixState.serviceDiversitySubCategoryFilter || [],
        serviceDensityCategoryFilter:
          serviceMixState.serviceDensityCategoryFilter || [],
        serviceDensitySubCategoryFilter:
          serviceMixState.serviceDensitySubCategoryFilter || [],
        competitivenessCategoryFilter:
          serviceMixState.competitivenessCategoryFilter || [],
        competitivenessSubCategoryFilter:
          serviceMixState.competitivenessSubCategoryFilter || [],
      };
    }

    case indicators.propertyMarket: {
      const { propertyMarketState } = legendState;
      if (!propertyMarketState) return {};

      return {
        selectedTab: propertyMarketState.selectedTab,
        selectedFillColorFilter:
          propertyMarketState.selectedFillColorFilter || [],
        selectedBuildingUseFilter:
          propertyMarketState.selectedBuildingUseFilter || [],
        selectedPropertyFilters:
          propertyMarketState.selectedPropertyFilters || [],
        selectedTransactionRange:
          propertyMarketState.selectedTransactionRange || [],
        selectedPropertyOwner: propertyMarketState.selectedPropertyOwner,
        showBuildings: propertyMarketState.showBuildings,
        showProperties: propertyMarketState.showProperties,
      };
    }

    case indicators.residentialMarket: {
      const { residentialMarketState } = legendState;
      if (!residentialMarketState) return {};

      return {
        selectedTab: residentialMarketState.selectedTab,
        rentAndAskingPriceSegment:
          residentialMarketState.rentAndAskingPriceSegment,
        rentAndAskingPriceDataset:
          residentialMarketState.rentAndAskingPriceDataset,
        selectedReferenceMarketMunicipality:
          residentialMarketState.selectedReferenceMarketMunicipality,
        selectedDwellingType: residentialMarketState.selectedDwellingType || [],
      };
    }

    case indicators.tenantSearch: {
      const { tenantSearchState } = legendState;
      if (!tenantSearchState) return {};

      return {
        selectedTab: tenantSearchState.selectedTab,
        selectedEmployeesFilter: tenantSearchState.selectedEmployeesFilter,
        selectedTurnoverFilter: tenantSearchState.selectedTurnoverFilter,
        selectedIndustriesFilter:
          tenantSearchState.selectedIndustriesFilter || [],
        colorTenantBy: tenantSearchState.colorTenantBy,
      };
    }

    case indicators.populationStructure: {
      const { populationStructureState } = legendState;
      if (!populationStructureState) return {};

      return {
        selectedTab: populationStructureState.selectedTab,
        selectedEssentialsDataset:
          populationStructureState.selectedEssentialsDataset,
        selectedEssentialsSegment:
          populationStructureState.selectedEssentialsSegment,
        selectedTopTrendDataset:
          populationStructureState.selectedTopTrendDataset,
        selectedTopTrendGroups:
          populationStructureState.selectedTopTrendGroups || [],
        selectedPopulationGridDataset:
          populationStructureState.selectedPopulationGridDataset,
      };
    }

    case indicators.footfall: {
      const { footfallState } = legendState;
      if (!footfallState) return {};

      return {
        selectedTab: footfallState.selectedTab,
        hubActivityFilter: footfallState.hubActivityFilter,
        activityScoreThreshold: footfallState.activityScoreThreshold,
      };
    }

    case indicators.households: {
      const { householdTypesState } = legendState;
      if (!householdTypesState) return {};

      return {
        selectedTab: householdTypesState.selectedTab,
        dataset: householdTypesState.dataset,
        datasetOptions: householdTypesState.datasetOptions || [],
        shareOf: householdTypesState.shareOf,
      };
    }

    default:
      return {};
  }
};
